/**
 * HreflangTags — injects <link rel="canonical"> and <link rel="alternate"
 * hreflang="…"> tags into <head> for every supported locale of the current
 * route.
 *
 * Google recommends bidirectional hreflang references. Each page declares
 * the URL of every locale variant (including itself) plus an x-default
 * pointing to the English variant.
 *
 * Locale is carried via the `?lang=<code>` query parameter. The English
 * variant is served at the bare URL (no query) so existing links keep
 * working.
 *
 * Alternates are only emitted for routes listed in translatedRoutes.json —
 * the single source of truth also read by the backend's sitemap generator
 * (backend/server.py), so the two never disagree about which pages are
 * genuinely translated vs. English-only content served under a `?lang=`
 * that doesn't actually change anything.
 *
 * Eligibility bar for that list: the page's MAIN content — not just its
 * hero, nav, or footer — must be translated. A page whose hero copy has a
 * `pages.<key>` entry in i18n.js but whose actual body (the list of
 * solutions/products/industries/etc., or a dynamically-fetched English-only
 * listing like Insights or open Careers roles) stays hardcoded English does
 * not qualify — that's exactly the gap a 2026-07-30 review caught (Home,
 * About, Technology, Research, Careers, Insights, Contact, and VDL were
 * wrongly listed despite only having translated hero copy). Only Solutions,
 * Industries, and Products render their entire item list from a translated
 * `body.<page>.items` array in i18n.js.
 *
 * Every other route canonicalises to its own bare English URL and skips
 * alternates entirely, rather than claiming untranslated `?lang=` variants
 * are real content. See docs/audits/SEO_AUDIT_2026-07-29.md (H2) and
 * docs/decisions/006-seo-analytics-and-indexing.md.
 *
 * `suppressCanonical` (wired through Layout's `notFound` prop) drops
 * canonical/alternates/og:url entirely instead of falling back to a bare
 * self-canonical — for the catch-all NotFound page and DetailNotFound
 * states, where the current URL doesn't resolve to real content and
 * shouldn't be declared canonical for itself.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGS } from "@/lib/i18n";
import TRANSLATED_ROUTES from "@/lib/translatedRoutes.json";

const BASE = "https://iventara.com";

// Map internal codes → BCP-47 hreflang codes.
const HREFLANG = { en: "en", hi: "hi", kn: "kn", ar: "ar" };

function absUrl(path, lang) {
    const url = new URL(BASE + (path || "/"));
    // Strip any pre-existing lang param, then set the fresh one for non-en.
    url.searchParams.delete("lang");
    if (lang && lang !== "en") url.searchParams.set("lang", lang);
    return url.toString();
}

function bareUrl(path) {
    return `${BASE}${path || "/"}`;
}

function upsertLink({ id, rel, href, hreflang }) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("link");
        el.id = id;
        document.head.appendChild(el);
    }
    el.setAttribute("rel", rel);
    el.setAttribute("href", href);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    else el.removeAttribute("hreflang");
}

function removeLink(id) {
    document.getElementById(id)?.remove();
}

function upsertMeta({ id, property, name, content }) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("meta");
        el.id = id;
        document.head.appendChild(el);
    }
    if (property) el.setAttribute("property", property);
    if (name) el.setAttribute("name", name);
    el.setAttribute("content", content);
}

export default function HreflangTags({ suppressCanonical = false } = {}) {
    const { pathname } = useLocation();
    const { i18n } = useTranslation();
    const active = (i18n.language || "en").split("-")[0];
    const isTranslated = TRANSLATED_ROUTES.includes(pathname);

    useEffect(() => {
        const clearOgLocaleAlternates = () => {
            document.querySelectorAll("meta[data-og-locale-alt='1']").forEach((n) => n.remove());
        };

        const update = () => {
            if (suppressCanonical) {
                // Invalid/error page (catch-all NotFound or a DetailNotFound
                // state): the URL doesn't resolve to real content, so don't
                // publish a canonical, hreflang alternates, or og:url for
                // it — and remove any that a previously-visited valid route
                // left behind, since this component stays mounted across
                // route changes. noindex is handled separately by the
                // page's own useNoIndex() call.
                removeLink("hreflang-canonical");
                LANGS.forEach((l) => removeLink(`hreflang-${l.code}`));
                removeLink("hreflang-x-default");
                document.getElementById("og-url")?.remove();
                clearOgLocaleAlternates();
                return;
            }

            if (!isTranslated) {
                // English-only route: canonicalise to the bare URL regardless
                // of any ?lang= in play, and remove any alternates a
                // previously-visited translated route may have left behind
                // (this component stays mounted across route changes).
                upsertLink({ id: "hreflang-canonical", rel: "canonical", href: bareUrl(pathname) });
                LANGS.forEach((l) => removeLink(`hreflang-${l.code}`));
                removeLink("hreflang-x-default");
                upsertMeta({ id: "og-locale", property: "og:locale", content: "en_US" });
                upsertMeta({ id: "og-url", property: "og:url", content: bareUrl(pathname) });
                clearOgLocaleAlternates();
                return;
            }

            const activeLng = (i18n.language || "en").split("-")[0];

            // Canonical → current locale URL.
            upsertLink({ id: "hreflang-canonical", rel: "canonical", href: absUrl(pathname, i18n.language) });
            LANGS.forEach((l) => {
                const code = HREFLANG[l.code] || l.code;
                upsertLink({
                    id: `hreflang-${l.code}`,
                    rel: "alternate",
                    href: absUrl(pathname, l.code),
                    hreflang: code,
                });
            });
            upsertLink({
                id: "hreflang-x-default",
                rel: "alternate",
                href: absUrl(pathname, "en"),
                hreflang: "x-default",
            });
            const map = { en: "en_US", hi: "hi_IN", kn: "kn_IN", ar: "ar_AE" };
            upsertMeta({ id: "og-locale", property: "og:locale", content: map[activeLng] || "en_US" });
            upsertMeta({ id: "og-url", property: "og:url", content: absUrl(pathname, i18n.language) });
            clearOgLocaleAlternates();
            LANGS.filter((l) => l.code !== activeLng).forEach((l) => {
                const m = document.createElement("meta");
                m.setAttribute("property", "og:locale:alternate");
                m.setAttribute("content", map[l.code] || l.code);
                m.setAttribute("data-og-locale-alt", "1");
                document.head.appendChild(m);
            });
        };
        update();
        // React-based deps (pathname, active, isTranslated) cover most cases,
        // but also hook into the i18n event so that transitions initiated via
        // the language switcher instantly rewrite the head tags.
        i18n.on("languageChanged", update);
        return () => { i18n.off("languageChanged", update); };
    }, [pathname, active, i18n, isTranslated, suppressCanonical]);

    return null;
}
